import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { track } from '@vercel/analytics';
import { CreatorUpdatesForm } from '../../components/CreatorUpdatesForm/CreatorUpdatesForm';
import { visibleToolCatalog, type ToolItem } from '../../data/toolCatalog';
import {
  googleExtensionShelf,
  type WarehouseShelfItem,
} from '../../data/warehouseCollections';
import { usePageMetadata } from '../../utils/usePageMetadata';
import * as S from './styles';

const allFilter = 'All';
const twoDesktopRows = 8;

const getBadgeLabel = (label?: string) => {
  if (!label) return 'Active';
  if (label === 'Adobe Exchange') return 'Plugin';
  if (label === 'Templates') return 'Template';
  return label;
};

const getProductIcon = (platform?: string, category?: string) => {
  if (platform === 'YouTube' || category === 'YouTube Tools') return 'bx bxl-youtube';
  if (platform === 'Spotify') return 'bx bxl-spotify';
  if (platform === 'Discord') return 'bx bxl-discord-alt';
  if (platform === 'Notion' || category === 'Templates') return 'bx bx-layout';
  if (platform === 'Gumroad') return 'bx bx-store';
  if (platform === 'Fourthwall') return 'bx bx-package';
  if (platform === 'Web' || category === 'Creator Utilities') return 'bx bx-wrench';
  if (category === 'Digital Products') return 'bx bx-book-open';
  if (category === 'Premiere Pro' || platform === 'Premiere Pro' || platform === 'Adobe Exchange') return 'bx bx-movie-play';
  return 'bx bx-cube';
};

const ShelfSection = ({
  title,
  items,
}: {
  title: string;
  items: WarehouseShelfItem[];
}) => (
  <S.ShelfSection aria-labelledby={`${title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}-title`}>
    <S.SectionHeader>
      <S.SectionTitle id={`${title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}-title`}>{title}</S.SectionTitle>
      <S.SectionCount>{items.length}</S.SectionCount>
    </S.SectionHeader>
    <S.ShelfTrack>
      {items.map(item => (
        <S.ShelfCard
          key={item.id}
          as={item.url ? 'a' : 'article'}
          href={item.url}
          target={item.url ? '_blank' : undefined}
          rel={item.url ? 'noopener noreferrer' : undefined}
          onClick={() => item.url && track('store_shelf_item_clicked', {
            item_id: item.id,
            platform: item.platform,
            source_page: 'store',
          })}
          aria-label={item.url ? `${item.title}: open ${item.platform}` : undefined}
        >
          <S.ShelfImage $image={item.image}>
            <S.ShelfImageAsset src={item.image} alt="" aria-hidden="true" />
          </S.ShelfImage>
          <S.ShelfContent>
            <S.ShelfTitle>{item.title}</S.ShelfTitle>
            <S.ShelfPlatform>{item.platform}</S.ShelfPlatform>
          </S.ShelfContent>
        </S.ShelfCard>
      ))}
    </S.ShelfTrack>
  </S.ShelfSection>
);

const ProductGrid = ({
  tools,
  onToolClick,
}: {
  tools: ToolItem[];
  onToolClick: (toolId: string, category: string, platform?: string) => void;
}) => (
  <S.ToolGrid>
    {tools.map(tool => (
      <S.ToolCard
        key={tool.id}
        href={tool.destinationUrl}
        target="_blank"
        rel="noopener noreferrer"
        onClick={() => onToolClick(tool.id, tool.category, tool.platform)}
        aria-label={`${tool.title}: ${tool.description || tool.category}`}
      >
        <S.ToolMedia $image={tool.image}>
          <S.PlatformBadge aria-label={tool.category}>
            <i className={getProductIcon(tool.platform, tool.category)} aria-hidden="true" />
          </S.PlatformBadge>
          {!tool.image && <i className="bx bx-cube" aria-hidden="true" />}
        </S.ToolMedia>
        <S.ToolContent>
          <S.ToolTitle>{tool.title}</S.ToolTitle>
          <S.ToolPlatform>{tool.category}</S.ToolPlatform>
          <S.ToolPrice>{tool.status === 'coming-soon' ? 'Coming Soon' : getBadgeLabel(tool.pricingLabel)}</S.ToolPrice>
        </S.ToolContent>
      </S.ToolCard>
    ))}
  </S.ToolGrid>
);

export const Store: React.FC = () => {
  const [activeCategory, setActiveCategory] = useState(allFilter);
  const [searchQuery, setSearchQuery] = useState('');
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const filterRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  usePageMetadata({
    title: 'The Warehouse | Austin Davenport',
    description: 'Browse Austin Davenport tools, Premiere Pro plugins, YouTube resources, templates, and creator utilities from one compact catalog.',
    path: '/store',
  });

  const categories = useMemo(
    () => [allFilter, ...Array.from(new Set(visibleToolCatalog.map(tool => tool.category))).sort()],
    []
  );

  const filteredTools = useMemo(() => {
    const normalizedSearch = searchQuery.trim().toLowerCase();

    return visibleToolCatalog.filter(tool => {
      const categoryMatches = activeCategory === allFilter || tool.category === activeCategory;
      const searchMatches = !normalizedSearch || [
        tool.title,
        tool.description,
        tool.category,
        tool.platform,
        tool.pricingLabel,
      ]
        .filter(Boolean)
        .some(value => value!.toLowerCase().includes(normalizedSearch));

      return categoryMatches && searchMatches;
    });
  }, [activeCategory, searchQuery]);

  const firstProductRows = filteredTools.slice(0, twoDesktopRows);
  const secondProductRows = filteredTools.slice(twoDesktopRows, twoDesktopRows * 2);
  const remainingProducts = filteredTools.slice(twoDesktopRows * 2);

  const handleCategoryChange = (category: string) => {
    setActiveCategory(category);
    setIsFilterOpen(false);
    track('store_filter_used', {
      category,
      source_page: 'store',
    });
  };

  useEffect(() => {
    if (!isFilterOpen) return;

    const handlePointerDown = (event: MouseEvent) => {
      if (event.target instanceof Node && !filterRef.current?.contains(event.target)) {
        setIsFilterOpen(false);
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsFilterOpen(false);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isFilterOpen]);

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    if (value.trim().length === 1 || value.trim().length % 5 === 0) {
      track('store_search_used', {
        query_length: value.trim().length,
        source_page: 'store',
      });
    }
  };

  const handleToolClick = (toolId: string, category: string, platform?: string) => {
    track('store_tool_clicked', {
      tool_id: toolId,
      category,
      platform: platform || null,
      destination_type: 'external',
      source_page: 'store',
    });
  };

  const handleBackClick = () => {
    if (window.history.length > 1) {
      navigate(-1);
      return;
    }

    navigate('/links');
  };

  return (
    <S.Container>
      <S.BackButton type="button" onClick={handleBackClick}>
        <i className="bx bx-arrow-back" aria-hidden="true" />
        Back
      </S.BackButton>

      <S.Header>
        <S.HeaderCopy>
          <S.Title>The Warehouse</S.Title>
        </S.HeaderCopy>
        <S.ProductCount>
          {filteredTools.length} {filteredTools.length === 1 ? 'product' : 'products'}
        </S.ProductCount>
      </S.Header>

      <S.Controls aria-label="Store controls">
        <S.SearchGroup>
          <S.SearchLabel htmlFor="store-search">Search</S.SearchLabel>
          <S.SearchInput
            id="store-search"
            type="search"
            value={searchQuery}
            onChange={event => handleSearchChange(event.target.value)}
            placeholder="Search tools"
          />
        </S.SearchGroup>

        <S.FilterGroup ref={filterRef}>
          <S.SearchLabel htmlFor="store-category">Filter</S.SearchLabel>
          <S.FilterButton
            id="store-category"
            type="button"
            aria-haspopup="listbox"
            aria-expanded={isFilterOpen}
            aria-controls="store-category-menu"
            onClick={() => setIsFilterOpen(open => !open)}
          >
            <span>{activeCategory}</span>
            <i className={isFilterOpen ? 'bx bx-chevron-up' : 'bx bx-chevron-down'} aria-hidden="true" />
          </S.FilterButton>
          {isFilterOpen && (
            <S.FilterMenu id="store-category-menu" role="listbox" aria-label="Filter products">
              {categories.map(category => (
                <S.FilterOption
                  key={category}
                  type="button"
                  role="option"
                  aria-selected={activeCategory === category}
                  $active={activeCategory === category}
                  onClick={() => handleCategoryChange(category)}
                >
                  <span>{category}</span>
                  {activeCategory === category && <i className="bx bx-check" aria-hidden="true" />}
                </S.FilterOption>
              ))}
            </S.FilterMenu>
          )}
        </S.FilterGroup>
      </S.Controls>

      <S.SectionHeader>
        <S.SectionTitle>All Products</S.SectionTitle>
        <S.SectionCount>{filteredTools.length}</S.SectionCount>
      </S.SectionHeader>

      {filteredTools.length ? (
        <>
          <S.ProductBlock>
            <ProductGrid tools={firstProductRows} onToolClick={handleToolClick} />
          </S.ProductBlock>

          <S.ShelfInterlude>
            <ShelfSection title="Google Extensions" items={googleExtensionShelf} />
          </S.ShelfInterlude>

          {secondProductRows.length > 0 && (
            <S.ProductBlock>
              <ProductGrid tools={secondProductRows} onToolClick={handleToolClick} />
            </S.ProductBlock>
          )}

          {remainingProducts.length > 0 && (
            <ProductGrid tools={remainingProducts} onToolClick={handleToolClick} />
          )}
        </>
      ) : (
        <S.EmptyState>No tools match that search.</S.EmptyState>
      )}

      <S.UpdatesWrap>
        <CreatorUpdatesForm source="source_store" showVisual />
      </S.UpdatesWrap>
    </S.Container>
  );
};

export default Store;
